import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { LeagueEntryStateChip } from '#src/components/league/LeagueEntryStateChip/index.tsx';
import { LeagueSelect } from '#src/components/league/LeagueSelect/index.tsx';
import { LeagueTrckGroupSelect } from '#src/components/league/LeagueTrckGroupSelect/index.tsx';
import { FixtureCompetitionTabs } from '#src/components/trckGroup/FixtureCompetitionTabs/index.tsx';
import { TrckGroupAsideGWSelector } from '#src/components/trckGroup/TrckGroupAsideGWSelector/index.tsx';

import type {
  TrckGroupAside_so5Fixture,
  TrckGroupAside_so5LeaderboardGroupInterface,
  TrckGroupAside_so5LeagueTrackGroup,
} from './__generated__/index.graphql.ts';

const Aside = styled(Vertical)`
  gap: var(--triple-unit);
  position: relative;
  isolation: isolate;

  @media ${laptopAndAbove} {
    padding-bottom: var(--double-unit);
  }

  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    max-height: calc(100vh - var(--current-stack-height));
  }
`;
const Top = styled.div`
  position: relative;
`;
const Picture = styled.img`
  width: 100%;
  object-position: top;
  object-fit: contain;
  height: 188px;
`;

const Selects = styled(FullWidthContainer)`
  --selects-height: calc(8 * var(--unit));

  position: sticky;
  z-index: 1;
  isolation: isolate;

  min-height: var(--selects-height);
  display: flex;
  gap: var(--unit);
  align-items: center;
  overflow: hidden;
  top: var(--current-stack-height);
  margin-top: calc((var(--selects-height) + var(--triple-unit)) * -1);

  @media ${desktopAndAbove} {
    top: 0;
  }

  .scrolledFurtherThanImage & {
    ${regularMaterial}
  }
`;

type Props = {
  so5Fixture: TrckGroupAside_so5Fixture;
  so5LeaderboardGroupInterface: TrckGroupAside_so5LeaderboardGroupInterface;
  so5LeagueTrackGroup: TrckGroupAside_so5LeagueTrackGroup;
  trck: string;
  children: ReactNode;
  header?: ReactNode;
  navigateTo: (props: {
    competition: string;
    trckGrp: string;
    trck: string;
  }) => void;
  overrideDescription?: ReactNode;
  activeSkeletons?: 'all' | 'rules' | false;
  allowNavigatingWhenLocked?: boolean;
  hideFixtureCompetitionTabs?: boolean;
  withGWSelector?: boolean;
  gwSelectorAasmStates?: string[];
  withCompetitionSelects?: boolean;
};

export const TrckGroupAside = ({
  so5Fixture,
  so5LeaderboardGroupInterface,
  so5LeagueTrackGroup,
  trck,
  children,
  header,
  navigateTo,
  withGWSelector,
  withCompetitionSelects = true,
  gwSelectorAasmStates,
  overrideDescription,
  activeSkeletons,
  allowNavigatingWhenLocked,
  hideFixtureCompetitionTabs,
}: Props) => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [topRef, { height: topHeight }] = useMeasure<HTMLDivElement>();
  const [selectsRef, { height: selectsHeight }] = useMeasure<HTMLDivElement>();
  const ref = useRef<HTMLDivElement>(null);
  const [scrolledFurtherThanImage, setScrolledFurtherThanImage] =
    useState(false);

  useEffect(() => {
    const onScrollMobile = () => {
      if (isDesktopAndAbove) {
        return;
      }

      if (selectsHeight) {
        setScrolledFurtherThanImage(window.scrollY >= selectsHeight);
      }
    };
    window.addEventListener('scroll', onScrollMobile);

    return () => {
      window.removeEventListener('scroll', onScrollMobile);
    };
  }, [isDesktopAndAbove, selectsHeight]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setScrolledFurtherThanImage(false);
  }, [so5LeagueTrackGroup]);

  return (
    <Aside
      ref={ref}
      onScroll={() => {
        if (isDesktopAndAbove && topHeight && ref.current) {
          setScrolledFurtherThanImage(ref.current.scrollTop >= topHeight);
        }
      }}
      className={classNames({ scrolledFurtherThanImage })}
      style={
        {
          '--header-background-url': `url(${activeSkeletons === 'all' ? '' : so5LeaderboardGroupInterface.headerBackgroundUrl})`,
        } as CSSProperties
      }
    >
      {!!withCompetitionSelects && (
        <Selects ref={selectsRef}>
          <LeagueSelect
            selectedSo5LeaderboardGroupSlug={so5LeaderboardGroupInterface.slug}
            so5LeaderboardGroupInterfaces={so5Fixture.competitionGroups}
            rarity={so5LeagueTrackGroup.mainRarityType || null}
            allowNavigatingWhenLocked={allowNavigatingWhenLocked}
            onClick={props => {
              navigateTo(props);
            }}
          />
          <LeagueTrckGroupSelect
            selectedSo5LeagueTrckGroupSlug={so5LeagueTrackGroup.slug}
            so5LeagueTrckGroups={
              so5LeaderboardGroupInterface.so5LeagueTrackGroups
            }
            allowNavigatingWhenLocked={allowNavigatingWhenLocked}
            onClick={props => {
              navigateTo({
                competition: so5LeaderboardGroupInterface.slug,
                ...props,
              });
            }}
          />
        </Selects>
      )}
      <Skeleton loading={activeSkeletons === 'all'}>
        <Top ref={topRef}>
          {!!so5LeaderboardGroupInterface.pictureUrl && (
            <Picture src={so5LeaderboardGroupInterface.pictureUrl} alt="" />
          )}
        </Top>
      </Skeleton>
      <FullWidthContainer>
        <Vertical gap={3}>
          <Horizontal>
            {withGWSelector ? (
              <TrckGroupAsideGWSelector
                navigateTo={navigateTo}
                selectedFixture={so5Fixture}
                aasmStates={gwSelectorAasmStates}
                so5LeaderboardType={
                  so5LeagueTrackGroup.so5LeagueTracks.at(0)?.entrySo5Leaderboard
                    ?.so5LeaderboardType
                }
              />
            ) : (
              !!so5Fixture && (
                <Horizontal>
                  <FontAwesomeIcon icon={iconCalendar} width={12} />
                  <LabelM bold>{so5Fixture.shortDisplayName}</LabelM>
                  <svg
                    width="2"
                    height="2"
                    viewBox="0 0 2 2"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="1" cy="1" r="1" fill="var(--c-nd-600)" />
                  </svg>
                  <LabelM color="var(--c-nd-600)">
                    {!!so5Fixture && (
                      <DateRange
                        startDate={so5Fixture.startDate}
                        endDate={so5Fixture.endDate}
                      />
                    )}
                  </LabelM>
                  <svg
                    width="2"
                    height="2"
                    viewBox="0 0 2 2"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="1" cy="1" r="1" fill="var(--c-nd-600)" />
                  </svg>
                </Horizontal>
              )
            )}
            <LabelM bold color="var(--c-yellow-400)">
              <LeagueEntryStateChip
                so5LeaderboardGroup={so5LeaderboardGroupInterface}
              />
            </LabelM>
          </Horizontal>
          <Skeleton loading={activeSkeletons === 'all'}>
            {overrideDescription || (
              <BodyM color="var(--c-nd-600)">
                {so5LeaderboardGroupInterface.description}
              </BodyM>
            )}
          </Skeleton>
          {header}
          {!hideFixtureCompetitionTabs && (
            <FixtureCompetitionTabs
              trck={trck}
              so5LeagueTracks={so5LeagueTrackGroup.so5LeagueTracks}
              onClick={({ trck: _trck }) => {
                navigateTo({
                  competition: so5LeaderboardGroupInterface.slug,
                  trckGrp: so5LeagueTrackGroup.slug,
                  trck: _trck,
                });
              }}
            />
          )}
          <Skeleton loading={!!activeSkeletons}>{children}</Skeleton>
        </Vertical>
      </FullWidthContainer>
    </Aside>
  );
};

TrckGroupAside.fragments = {
  so5Fixture: gql`
    fragment TrckGroupAside_so5Fixture on So5Fixture {
      slug
      shortDisplayName
      startDate
      endDate
      competitionGroups: so5LeaderboardGroups(groupType: $groupType) {
        slug
        ...LeagueSelect_so5LeaderboardGroupInterface
        ...LeagueEntryStateChip_so5LeaderboardGroupInterface
      }
      ...TrckGroupAsideGWSelector_so5Fixture
    }
    ${LeagueSelect.fragments.so5LeaderboardGroupInterface}
    ${LeagueTrckGroupSelect.fragments.so5LeagueTrackGroup}
    ${TrckGroupAsideGWSelector.fragments.so5Fixture}
    ${FixtureCompetitionTabs.fragments.so5LeagueTrack}
    ${LeagueEntryStateChip.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<TrckGroupAside_so5Fixture>,
  so5LeaderboardGroupInterface: gql`
    fragment TrckGroupAside_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      description
      headerBackgroundUrl
      pictureUrl
      so5LeagueTrackGroups {
        slug
        ...LeagueTrckGroupSelect_so5LeagueTrackGroup
      }
      ...LeagueEntryStateChip_so5LeaderboardGroupInterface
    }
    ${LeagueTrckGroupSelect.fragments.so5LeagueTrackGroup}
    ${LeagueEntryStateChip.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<TrckGroupAside_so5LeaderboardGroupInterface>,
  so5LeagueTrackGroup: gql`
    fragment TrckGroupAside_so5LeagueTrackGroup on So5LeagueTrackGroup {
      slug
      mainRarityType
      so5LeagueTracks {
        slug
        entrySo5Leaderboard {
          slug
          so5LeaderboardType
        }
        ...FixtureCompetitionTabs_so5LeagueTrack
      }
    }
    ${FixtureCompetitionTabs.fragments.so5LeagueTrack}
  ` as TypedDocumentNode<TrckGroupAside_so5LeagueTrackGroup>,
};
